import { ConnectionState, IRawMessageIn } from '../types';
import { BaseEventType } from '../types/enums/ProviderEventType';
import { CloseEventType } from '../types/enums/CloseEventType';
import {
	ProviderCloseEvent,
	ProviderErrorEvent,
	ProviderEvent,
	ProviderMessageEvent,
} from '../models/ProviderEvents';
import { BaseChatProvider } from './BaseChatProvider';
import { IConnectionConfiguration } from 'src/api/configuration/types';

export class ChatWS extends BaseChatProvider {
	protected _connection?: WebSocket;
	protected _reconnect?: boolean;

	constructor(
		protected baseUrl: string,
		protected configuration: Partial<IConnectionConfiguration>,
	) {
		super();
		if (!configuration.websocket_url) {
			throw new Error('You must provide a valid HTTP url');
		}
	}

	get url() {
		const url = new URL(this.configuration.websocket_url!, this.baseUrl);
		url.protocol = 'wss';
		if (process.env.NODE_ENV === 'development') {
			url.protocol = 'ws';
		}
		return url.href;
	}

	get connection() {
		if (!this._connection) {
			throw new Error(`No WS connection.`);
		} else {
			return this._connection;
		}
	}

	init(tstamp?: string) {
		if (this._connection) {
			this.close();
		}

		const url = this.url + (tstamp ? `?timestamp=${tstamp}` : '');
		// When the server issued a JWT, offer it as the websocket
		// subprotocol: browsers attach it to the handshake without relying
		// on cookies (which e.g. Safari blocks for third-party embeds).
		// Servers that do not issue a JWT keep using the cookie-only flow.
		const protocols = this.configuration.jwt
			? [this.configuration.jwt]
			: undefined;
		const connection = new WebSocket(url, protocols);
		this._connection = connection;
		let opened = false;

		// bind listener
		connection.addEventListener('open', (ev: Event) => {
			opened = true;
			this.notify(new ProviderEvent(BaseEventType.OPEN, ev));
		});
		connection.addEventListener('error', (ev: Event) => {
			this.notify(new ProviderErrorEvent(ev));
		});
		connection.addEventListener('message', (ev: MessageEvent) => {
			const chatMessageEvent = new ProviderMessageEvent(ev);
			// notify MessageEvent
			this.notify(chatMessageEvent);
		});
		connection.addEventListener('close', (ev: CloseEvent) => {
			// console.log('WebSocket closed', ev);
			let reason: CloseEventType = Object.values(CloseEventType).includes(
				ev.reason as CloseEventType,
			)
				? (ev.reason as CloseEventType)
				: CloseEventType.NOT_DEFINED;
			if (reason === CloseEventType.NOT_DEFINED) {
				if (!opened && !ev.wasClean && ev.code === 1006) {
					// Browser APIs deliberately hide the HTTP status of a failed
					// websocket handshake. Distinguish only the observable fact that
					// this connection never reached OPEN, so callers may refresh an
					// expired/stale page-scoped credential once before normal retries.
					reason = CloseEventType.CONNECTION_FAILED;
				} else if (ev.wasClean) {
					reason = CloseEventType.CLIENT_CLOSE;
				} else if (!ev.wasClean && ev.code === 1006) {
					reason = CloseEventType.CONNECTION_LOST;
				}
			}

			this.notify(new ProviderCloseEvent(ev, reason));
			if (this._connection === connection) {
				this._connection = undefined;
			}
		});
	}

	close(code?: number, reason?: CloseEventType) {
		try {
			if (this._connection) {
				this.connection.close(code, reason);
			}
		} catch (error) {
			this.notify(new ProviderErrorEvent(error));
		}
		this._connection = undefined;
	}
	clear() {
		this.listeners = {};
		return this.close();
	}

	get state() {
		switch (this._connection?.readyState) {
			case 0:
				return ConnectionState.CONNECTING;
			case 1:
				return ConnectionState.CONNECTED;
			case 2:
				return ConnectionState.CLOSING;
			case 3:
				return ConnectionState.CLOSED;
			default:
				return ConnectionState.TO_CONNECT;
		}
	}

	sendMessage<T extends IRawMessageIn>(
		rawIn: T,
		count = this.configuration.reconnection_retries ?? 5,
	): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if (this.state !== ConnectionState.CONNECTED) {
				reject(new Error('Connection still closed', { cause: rawIn }));
			} else {
				resolve(this.connection.send(JSON.stringify(rawIn)));
			}
		}).catch((e) => {
			this.notify(new ProviderErrorEvent(e));
			if (count > 0) {
				return new Promise<void>((resolve) => {
					// console.log('Resending message');
					setTimeout(() => {
						resolve(this.sendMessage(rawIn, count - 1));
					}, this.configuration.reconnection_timeout ?? 10000);
				});
			} else {
				throw new Error('Send message max retry');
			}
		});
	}
}
